/**
 * The address inside what a QR code holds: a bare address, or a payment link
 * around one — "bitcoin:bc1…?amount=…", "ethereum:0x…@1/transfer…",
 * "tron:T…", "ton://transfer/EQ…?amount=…", and EIP-681 ERC-20 transfers,
 * "ethereum:<token contract>@1/transfer?address=<recipient>&uint256=…".
 *
 * The scanner used to take whatever followed the last ":". For a TON link
 * that is "//transfer/EQ…", which passed the length-only TON check as an
 * address; and a raw TON address, "0:<64 hex>", lost its workchain.
 */
export const parseScannedAddress = (raw: string): string => {
  const value = raw.trim();
  if (!value) {
    return '';
  }

  // A raw TON address is "workchain:hex" — the colon belongs to it.
  if (/^-?\d+:[0-9a-fA-F]{64}$/.test(value)) {
    return value;
  }

  // "scheme://action/address?…" — TON's ton://transfer/<address>, and links
  // written the same way: the address is the last path segment.
  const hierarchical = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/(.*)$/.exec(value);
  if (hierarchical) {
    const path = hierarchical[2].split(/[?#]/)[0];
    // ton://transfer/<address>: the address is everything after "transfer/"
    // — a TON address in standard base64 may itself contain "/".
    const ton = /^transfer\/(.+)$/i.exec(path);
    if (hierarchical[1].toLowerCase() === 'ton' && ton) {
      return decodeSafe(ton[1]);
    }
    const segments = path.split('/').filter(Boolean);
    return decodeSafe(segments[segments.length - 1] ?? '');
  }

  // "scheme:address?…" — BIP-21 and the like.
  const opaque = /^[a-zA-Z][a-zA-Z0-9+.-]*:(.*)$/.exec(value);
  if (opaque) {
    const [target, query = ''] = opaque[1].split('#')[0].split('?');
    // EIP-681: "ethereum:[pay-]<target>[@chainId][/function]?params". With a
    // function the target is the token CONTRACT — "…/transfer?address=<to>"
    // sends tokens to <to>. Taking the target there would send the funds to
    // the contract; the recipient is the `address` parameter, and without one
    // nothing usable is returned, so the address check fails.
    const slash = target.indexOf('/');
    if (slash >= 0) {
      const fn = target.slice(slash + 1);
      if (fn !== 'transfer') {
        return '';
      }
      const recipient = new URLSearchParamsLite(query).get('address');
      return recipient ? decodeSafe(recipient) : '';
    }
    return decodeSafe(target.replace(/^pay-/, '').split('@')[0]);
  }

  return value;
};

const decodeSafe = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/**
 * A TON address: user-friendly — 48 characters of base64, url-safe (EQ…,
 * UQ…, with - and _) or standard (with + and /) — or raw,
 * "workchain:<64 hex>". The check used to accept any 48 characters.
 */
export const isTonAddress = (address: string): boolean =>
  /^[A-Za-z0-9_-]{48}$/.test(address)
  || /^[A-Za-z0-9+/]{48}$/.test(address)
  || /^-?\d+:[0-9a-fA-F]{64}$/.test(address);

/** The query part of a payment link — URLSearchParams is not on every RN runtime. */
class URLSearchParamsLite {
  private readonly pairs: [string, string][];
  constructor(query: string) {
    this.pairs = query.split('&').filter(Boolean).map((part) => {
      const eq = part.indexOf('=');
      return eq < 0 ? [part, ''] : [part.slice(0, eq), part.slice(eq + 1)];
    });
  }
  get(name: string): string | null {
    const hit = this.pairs.find(([key]) => key === name);
    return hit ? hit[1] : null;
  }
}
